import { operationIds } from "@gen/app-access-config";
import {
  CoreQuery,
  type IAdminTransaction,
  SetF2fTransactionNoteBody,
  SetF2fTransactionOwnerBody,
  SetOnlineTransactionNoteBody,
} from "@gen/app-query";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";

import { PublicSvgIcon } from "~/components/public_svg_icon";
import { ClearableSelectWithPlaceholder } from "~/components/select_with_placeholder";
import { useAuthOperations } from "~/contexts/auth_query";

interface NoteSectionProps {
  transactionData: IAdminTransaction;
  isF2f?: boolean;
}

interface FormValues {
  note: string;
  owner: string;
}

export function NoteSection({ transactionData, isF2f }: NoteSectionProps) {
  const { canExecuteAdminOperation } = useAuthOperations();
  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      note: transactionData.note ?? "",
      owner: transactionData.owner_email ?? "",
    },
  });

  const transactionId = transactionData.id;
  const queryClient = useQueryClient();

  const {
    mutate: setF2fTransactionNote,
    isPending: isSettingF2fTransactionNote,
  } = CoreQuery.useAdmin_setF2fTransactionNoteMutation(transactionId, {
    meta: {
      success: "Note saved successfully",
    },
    onSuccess: () => {
      if (transactionId) {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
        });
      }
    },
  });
  const {
    mutate: setOnlineTransactionNote,
    isPending: isSettingOnlineTransactionNote,
  } = CoreQuery.useAdmin_setOnlineTransactionNoteMutation(transactionId, {
    meta: {
      success: "Note saved successfully",
    },
    onSuccess: () => {
      if (transactionId) {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getOnlineTransactionQueryKey(transactionId),
        });
      }
    },
  });
  const { data: adminUsersData } = CoreQuery.useAdmin_getAdminUsersQuery();

  const {
    mutate: setF2fTransactionOwner,
    isPending: isSettingF2fTransactionOwner,
  } = CoreQuery.useAdmin_setF2fTransactionOwnerMutation(transactionId, {
    meta: {
      success: "Owner saved successfully",
    },
    onSuccess: () => {
      if (transactionId) {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
        });
      }
    },
  });

  const {
    mutate: setOnlineTransactionOwner,
    isPending: isSettingOnlineTransactionOwner,
  } = CoreQuery.useAdmin_setOnlineTransactionOwnerMutation(transactionId, {
    meta: {
      success: "Owner saved successfully",
    },
    onSuccess: () => {
      if (transactionId) {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getOnlineTransactionQueryKey(transactionId),
        });
      }
    },
  });

  const isDisabled = !isDirty;

  const onSubmit = useCallback(
    (values: FormValues) => {
      if (isF2f) {
        if (dirtyFields.note) {
          setF2fTransactionNote(
            SetF2fTransactionNoteBody.fromJS({
              note: values.note,
            }),
          );
        }
        if (dirtyFields.owner) {
          setF2fTransactionOwner(
            SetF2fTransactionOwnerBody.fromJS({
              owner: values.owner,
            }),
          );
        }
      } else {
        if (dirtyFields.note) {
          setOnlineTransactionNote(
            SetOnlineTransactionNoteBody.fromJS({
              note: values.note,
            }),
          );
        }
        if (dirtyFields.owner) {
          setOnlineTransactionOwner(
            SetF2fTransactionOwnerBody.fromJS({
              owner: values.owner,
            }),
          );
        }
      }
    },
    [
      isF2f,
      dirtyFields.note,
      dirtyFields.owner,
      setOnlineTransactionNote,
      setOnlineTransactionOwner,
      setF2fTransactionOwner,
      setF2fTransactionNote,
    ],
  );

  const isLoading =
    isSettingOnlineTransactionOwner ||
    isSettingOnlineTransactionNote ||
    isSettingF2fTransactionOwner ||
    isSettingF2fTransactionNote;

  const canSaveNote = canExecuteAdminOperation(
    operationIds["core_admin.setF2fTransactionNote"],
  );

  return (
    <Stack
      spacing={5}
      useFlexGap
      sx={{
        mb: 5,
        p: 3,
        backgroundColor: "primary._states.hover",
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h5SolidDivider"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <PublicSvgIcon name="add_notes" /> Note
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={5}>
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                rows={4}
                label="Add note here"
                fullWidth
                disabled={!canSaveNote}
                slotProps={{ htmlInput: { "data-testid": "note-input" } }}
              />
            )}
          />

          <Controller
            name="owner"
            control={control}
            render={({ field }) => (
              <FormControl size="small">
                <InputLabel>Owner</InputLabel>
                <ClearableSelectWithPlaceholder
                  {...field}
                  label="owner"
                  placeholder="Choose owner"
                  disabled={!canSaveNote}
                  data-testid="note-owner-select"
                >
                  {adminUsersData?.users.map(({ email }) => (
                    <MenuItem
                      key={email}
                      value={email}
                      data-testid={`owner-${email}-option`}
                    >
                      {email}
                    </MenuItem>
                  ))}
                </ClearableSelectWithPlaceholder>
              </FormControl>
            )}
          />

          {canSaveNote && (
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={isDisabled}
              loading={isLoading}
              data-testid="save-note-button"
            >
              Save
            </Button>
          )}
        </Stack>
      </form>
    </Stack>
  );
}
